import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { bookings, tickets, ticketSegments } from '../../services/api';
import { formatDate } from '../../utils/dateFormat';
import { ticketStatuses, ticketStatusColors } from '../../constants/travelEnums';

function MyBookings() {
  const navigate = useNavigate();
  const location = useLocation();
  const [allBookings, setAllBookings] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [allSegments, setAllSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(() => {
    // Try to get filter from location state first, then from localStorage
    return location.state?.filter || localStorage.getItem('bookingsFilter') || 'current';
  }); // 'current', 'past'

  useEffect(() => {
    // Save filter to localStorage whenever it changes
    localStorage.setItem('bookingsFilter', filter);
  }, [filter]);

  useEffect(() => {
    // If we have a filter in location state, update it
    if (location.state?.filter) {
      setFilter(location.state.filter);
    }
  }, [location.state?.filter]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, ticketsRes, segmentsRes] = await Promise.all([
        bookings.getAll(),
        tickets.getAll(),
        ticketSegments.getAll()
      ]);
      setAllBookings(bookingsRes.data);
      setAllTickets(ticketsRes.data);
      setAllSegments(segmentsRes.data);
      setError(null);
    } catch (err) {
      setError('Failed to load bookings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Get only root bookings (without parent)
  const rootBookings = allBookings.filter(b => !b.booking_parent_id);

  // Get all booking IDs in hierarchy (booking + all nested)
  const getAllBookingIds = (rootId) => {
    const ids = [rootId];
    const findNested = (parentId) => {
      const nested = allBookings.filter(b => b.booking_parent_id === parentId);
      nested.forEach(n => {
        ids.push(n.booking_id);
        findNested(n.booking_id);
      });
    };
    findNested(rootId);
    return ids;
  };

  // Get all segments for a booking hierarchy
  const getAllSegmentsForHierarchy = (rootId) => {
    const bookingIds = getAllBookingIds(rootId);
    const hierarchyTickets = allTickets.filter(t => bookingIds.includes(t.ticket_booking_id));
    const ticketIds = hierarchyTickets.map(t => t.ticket_id);
    return allSegments.filter(s => ticketIds.includes(s.tsegment_ticket_id));
  };

  // Check if booking is "current" (has at least one active segment without usage result)
  const isCurrentBooking = (booking) => {
    const hierarchySegments = getAllSegmentsForHierarchy(booking.booking_id);

    // Current: at least one segment with status 10 (active) and no usage_result
    return hierarchySegments.some(s =>
      s.tsegment_status === 10 && !s.tsegment_usage_result
    );
  };

  // Check if booking is "past" (all other bookings)
  const isPastBooking = (booking) => {
    return !isCurrentBooking(booking);
  };

  const getFilteredBookings = () => {
    if (filter === 'current') {
      return rootBookings.filter(isCurrentBooking);
    } else {
      return rootBookings.filter(isPastBooking);
    }
  };

  const filteredBookings = getFilteredBookings();
  const currentCount = rootBookings.filter(isCurrentBooking).length;
  const pastCount = rootBookings.filter(isPastBooking).length;

  // Get segment summary for a booking
  const getBookingSegments = (bookingId) => {
    const bookingTickets = allTickets.filter(t => t.ticket_booking_id === bookingId);
    const ticketIds = bookingTickets.map(t => t.ticket_id);
    return allSegments.filter(s => ticketIds.includes(s.tsegment_ticket_id) && s.tsegment_status === 10);
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h2>My Bookings</h2>
        </div>
        <p>Loading bookings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="page-header">
          <h2>My Bookings</h2>
        </div>
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>My Bookings</h2>
        <button className="btn btn-primary" onClick={() => navigate('/bookings')}>
          Manage Bookings
        </button>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          className={`btn ${filter === 'current' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('current')}
        >
          Current Bookings ({currentCount})
        </button>
        <button
          className={`btn ${filter === 'past' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('past')}
        >
          Past Bookings ({pastCount})
        </button>
      </div>

      {filteredBookings.length === 0 ? (
        <div style={{ background: 'white', padding: '40px', borderRadius: '8px', textAlign: 'center', color: '#7f8c8d' }}>
          <p>No bookings found for this filter.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '15px' }}>
          {filteredBookings.map((booking) => {
            const segments = getBookingSegments(booking.booking_id);
            return (
              <div
                key={booking.booking_id}
                className="booking-card"
                style={{
                  background: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/booking-details/${booking.booking_id}`, { state: { filter } })}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <h3 style={{ margin: 0, fontSize: '1.4em', color: '#2c3e50' }}>
                        {booking.booking_pnr}
                        {(booking.agent_name || booking.carrier_name) && (
                          <span style={{ fontSize: '0.85em', color: '#7f8c8d', fontWeight: 'normal', marginLeft: '8px' }}>
                            ({booking.agent_name || booking.carrier_name})
                          </span>
                        )}
                      </h3>
                      <span className={`badge ${ticketStatusColors[booking.booking_status]}`}>
                        {ticketStatuses[booking.booking_status]}
                      </span>
                    </div>

                    <div style={{ color: '#7f8c8d', fontSize: '0.95em', marginBottom: '10px' }}>
                      <div><strong>Date:</strong> {formatDate(booking.booking_date)}</div>
                      {booking.agent_name && <div><strong>Agent:</strong> {booking.agent_name}</div>}
                      {booking.carrier_name && <div><strong>Carrier:</strong> {booking.carrier_name}</div>}
                    </div>
                  </div>

                  <button
                    className="btn btn-sm btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/booking-details/${booking.booking_id}`, { state: { filter } });
                    }}
                    style={{ padding: '8px 16px' }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MyBookings;
